import {validate} from 'uuid';

function isUuidFormat(maybeUuid: string): boolean {
  if (!maybeUuid) {
    return false;
  }

  return validate(maybeUuid);
}

export function getMaybeUuid(): string | undefined {
  return window.location.pathname
    .split(`/`)
    .filter((x) => x)
    .slice(-1)
    .find((x) => x);
}

export function getPublicDashboardUuid(maybeUuid: string = getMaybeUuid()): string | null {
  if (isUuidFormat(maybeUuid)) {
    return maybeUuid;
  }

  try {
    const fromBase58 = base58StringToUuid(maybeUuid);
    if (isUuidFormat(fromBase58)) {
      return fromBase58;
    }
  } catch (err) {
    // base 58 parsing failed, swallow the error and return null
  }
  return null;
}

//#region Uint8Array
/* https://stackoverflow.com/questions/38987784/how-to-convert-a-hexadecimal-string-to-uint8array-and-back-in-javascript */
function fromHexString(hexString: string): Uint8Array {
  return new Uint8Array(hexString.match(/.{1,2}/g).map((byte) => parseInt(byte, 16)));
}

function toHexString(bytes: Uint8Array): string {
  return bytes.reduce((str, byte) => str + byte.toString(16).padStart(2, `0`), ``);
}
//#endregion

//#region base58
/* https://gist.github.com/diafygi/90a3e80ca1c2793220e5/ */
export const base58alphabet = `123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz`;
function toBase58(rawBytes: Uint8Array) {
  const digits = []; //the array for storing the stream of base58 digits
  let result = ``, //the result string variable that will be returned
    byteIterator, //the iterator variable for the byte input
    digitIterator, //the iterator variable for the base58 digit array (d)
    carry, //the carry amount variable that is used to overflow from the current base58 digit to the next base58 digit
    temp; //a temporary placeholder variable for the current base58 digit
  for (byteIterator in rawBytes) {
    //loop through each byte in the input stream
    digitIterator = 0; //reset the base58 digit iterator
    carry = rawBytes[byteIterator]; //set the initial carry amount equal to the current byte amount
    result += carry || result.length ^ byteIterator ? `` : 1; //prepend the result string with a "1" (0 in base58) if the byte stream is zero and non-zero bytes haven't been seen yet (to ensure correct decode length)
    while (digitIterator in digits || carry) {
      //start looping through the digits until there are no more digits and no carry amount
      temp = digits[digitIterator]; //set the placeholder for the current base58 digit
      temp = temp ? temp * 256 + carry : carry; //shift the current base58 one byte and add the carry amount (or just add the carry amount if this is a new digit)
      carry = (temp / 58) | 0; //find the new carry amount (floored integer of current digit divided by 58)
      digits[digitIterator] = temp % 58; //reset the current base58 digit to the remainder (the carry amount will pass on the overflow)
      digitIterator++; //iterate to the next base58 digit
    }
  }
  while (
    digitIterator-- //since the base58 digits are backwards, loop through them in reverse order
  )
    result += base58alphabet[digits[digitIterator]]; //lookup the character associated with each base58 digit
  return result; //return the final base58 string
}
function fromBase58(encodedString: string) {
  const decodedBytes = []; //the array for storing the stream of decoded bytes
  const resultByteArray = []; //the result byte array that will be returned
  let byteIterator, //the iterator variable for the byte array (d)
    carry, //the carry amount variable that is used to overflow from the current byte to the next byte
    temp; //a temporary placeholder variable for the current byte

  for (let stringIterator = 0; stringIterator < encodedString.length; stringIterator++) {
    //loop through each base58 character in the input string
    byteIterator = 0; //reset the byte iterator
    carry = base58alphabet.indexOf(encodedString[stringIterator]); //set the initial carry amount equal to the current base58 digit
    //see if the base58 digit lookup is invalid (-1)
    if (carry < 0) {
      return undefined; //if invalid base58 digit, bail out and return undefined
    }
    if (!(carry || resultByteArray.length ^ stringIterator)) {
      resultByteArray.push(0); //prepend the result array with a zero if the base58 digit is zero and non-zero characters haven't been seen yet (to ensure correct decode length)
    }

    while (byteIterator in decodedBytes || carry) {
      //start looping through the bytes until there are no more bytes and no carry amount
      temp = decodedBytes[byteIterator]; //set the placeholder for the current byte
      temp = temp ? temp * 58 + carry : carry; //shift the current byte 58 units and add the carry amount (or just add the carry amount if this is a new byte)
      carry = temp >> 8; //find the new carry amount (1-byte shift of current byte value)
      decodedBytes[byteIterator] = temp % 256; //reset the current byte to the remainder (the carry amount will pass on the overflow)
      byteIterator++; //iterate to the next byte
    }
  }

  while (
    byteIterator-- //since the byte array is backwards, loop through it in reverse order
  )
    resultByteArray.push(decodedBytes[byteIterator]); //append each byte to the result
  return new Uint8Array(resultByteArray); //return the final byte array in Uint8Array format
}
//#endregion

//#region uuid-conversions
function uuidToHexString(uuid: string) {
  return uuid.replace(/-/g, ``);
}
function hexStringToUuid(hex: string) {
  // NOTE: Python B58 setup we're transitioning to doesn't play nicely with the setup above
  // hex string can have too many 0s prepended. Fix is to just make it the right length.
  // B58 logic should be removed from FE shortly
  hex = hex.substr(hex.length - 32);
  return `${hex.substr(0, 8)}-${hex.substr(8, 4)}-${hex.substr(12, 4)}-${hex.substr(16, 4)}-${hex.substr(20)}`;
}
//#endregion

function isB58String(maybeB58: string): boolean {
  return maybeB58.search(new RegExp(`^[${base58alphabet}]{16,22}$`)) === 0;
}

export function uuidToBase58String(uuid: string) {
  // NOTE: possibly being fed in a B58 here during transition, check and just return if that's the case
  if (isB58String(uuid)) {
    return uuid;
  }
  return toBase58(fromHexString(uuidToHexString(uuid)));
}

export function base58StringToUuid(b58: string) {
  return hexStringToUuid(toHexString(fromBase58(b58)));
}
